import { clip, type Bones, type Clip } from '../clip.ts';
import { TAU, envelope, inCubic, inExpo, inOut, inQuad, outBack, outCubic, outExpo, outQuad, phase, settle, span } from '../curves.ts';
import { apply, timeline, type KeyPose } from '../pose.ts';

/*
 * La Sima's nightmares (models/sima.ts). Every one has idle and walk (loops), attack (it starts cocked and lands
 * at ~0.13 s, when the simulation deals the blow), hit, stun (loop) and die; the scribe also `cast` (it writes a
 * rune in the air with both hands up).
 *
 * Limbs as in human.ts: −X swings an arm or leg forward, +X on a shin bends the knee, −X on a forearm bends the
 * elbow, +X on a trunk leans it forward, +Z on the right arm lifts it out. Worms: −X on a front segment lifts it,
 * +X on a rear segment lifts it, a Y turn swings it sideways. Crystal legs as the spiderling's.
 */

const kick = (t: number, t0: number, freq = 2.5, damp = 7): number => (t < t0 ? 0 : settle(t - t0, freq, damp));
const thud = (t: number, hits: readonly [number, number][]): [number, number, number] => {
  let k = 0;
  for (const [t0, a] of hits) k += a * Math.max(0, kick(t, t0, 3, 9));
  return [1 + 0.6 * k, 1 - k, 1 + 0.6 * k];
};

/* ======================================================================================================== */
/* Leech                                                                                                     */
/* ======================================================================================================== */

type LB = 'root' | 'body' | 'fore' | 'head' | 'hind' | 'tail';
type LP = KeyPose<LB>;

/** Its rest: the front just off the ground, as if tasting the air. */
const L_ST: LP = { fore: [-0.12, 0, 0], head: [-0.1, 0, 0] };

/** A sideways ripple along the body (`a` radians, phase `th`). */
const ripple = (b: Bones<LB>, th: number, a: number): void => {
  b.head.rot(0, a * Math.sin(th - 1.6), 0);
  b.fore.rot(0, a * Math.sin(th - 0.8), 0);
  b.body.rot(0, a * 0.6 * Math.sin(th), 0);
  b.hind.rot(0, -a * Math.sin(th + 0.8), 0);
  b.tail.rot(0, -a * Math.sin(th + 1.6), 0);
};

const leechIdle = (): Clip<LB> =>
  clip<LB>('idle', 2.4, true, (b, t) => {
    const p = phase(t, 2.4) * TAU;
    apply(b, L_ST);
    // The front lifts and sways, searching; the body throbs as it drinks the air.
    const up = 0.5 + 0.5 * Math.sin(p);
    b.fore.rot(-0.25 * up, 0.25 * Math.sin(p * 2), 0);
    b.head.rot(-0.2 * up, 0.35 * Math.sin(p * 2 + 0.6), 0);
    b.body.scale(1 + 0.04 * Math.sin(p * 3));
    b.hind.scale(1 + 0.05 * Math.sin(p * 3 - 0.8));
    b.tail.rot(0, 0.1 * Math.sin(p), 0);
  });

/** It swims over the floor: a ripple running down it, the front bobbing. */
const leechWalk = (dur = 0.6): Clip<LB> =>
  clip<LB>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    apply(b, L_ST);
    ripple(b, th, 0.42);
    b.fore.rot(-0.08 * (1 + Math.sin(th)), 0, 0);
    b.head.rot(0.06 * Math.sin(th - 0.8), 0, 0);
    b.hind.rot(0.06 * (1 + Math.sin(th + 1.2)), 0, 0);
    b.root.pos(0, 0.006 * Math.abs(Math.sin(th)), 0);
  }, (t) => {
    const s = Math.sin(phase(t, dur) * TAU * 2);
    return [1 - 0.04 * s, 1 + 0.03 * s, 1 + 0.06 * s];
  });

const L_REAR: LP = { body: [-0.12, 0, 0], fore: [-0.65, 0, 0], head: [-0.25, 0, 0], hind: [-0.05, 0, 0], root: [0, 0, 0, 0, 0, -0.04] };
const L_LATCH: LP = { body: [0.05, 0, 0], fore: [0.18, 0, 0], head: [0.3, 0, 0], hind: [0.05, 0, 0], root: [0, 0, 0, 0, 0, 0.1] };

/** Reared, it strikes and latches at 0.13 s, sucks twice, and lets go. */
const leechAttack = (): Clip<LB> =>
  clip<LB>('attack', 0.6, false, (b, t) => {
    timeline(b, t, [
      [0, L_REAR],
      [0.06, { ...L_REAR, fore: [-0.75, 0, 0] }, outQuad],
      [0.13, L_LATCH, inExpo],
      [0.42, { ...L_LATCH, root: [0, 0, 0, 0, 0, 0.08] }, inOut],
      [0.6, L_ST, inOut],
    ], { head: 0.02, tail: 0.04 });
    const suck = envelope(t, 0.15, 0.2, 0.38, 0.45) * Math.sin(TAU * 6 * t);
    b.body.scale(1 + 0.07 * suck);
    b.hind.scale(1 - 0.06 * suck);
  }, (t) => thud(t, [[0.13, 0.08]]));

const leechHit = (): Clip<LB> =>
  clip<LB>('hit', 0.35, false, (b, t) => {
    apply(b, L_ST);
    const k = settle(t, 3, 10) * span(t, 0, 0.02, outQuad);
    b.root.pos(0, 0, -0.04 * k);
    b.fore.rot(-0.3 * k, 0.3 * k, 0);
    b.head.rot(-0.2 * k, 0.3 * k, 0);
    b.tail.rot(0.3 * k, -0.3 * k, 0);
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 10));
    return [1 + 0.06 * k, 1 - 0.1 * k, 1 - 0.06 * k];
  });

const leechStun = (): Clip<LB> =>
  clip<LB>('stun', 1.6, true, (b, t) => {
    const th = phase(t, 1.6) * TAU;
    ripple(b, th, 0.2);
    b.fore.rot(0.1, 0, 0);
    b.head.rot(0.15, 0, 0.3 * Math.sin(th));
  });

/** It writhes, curls into a ring and shrivels. */
const leechDie = (): Clip<LB> =>
  clip<LB>('die', 1.2, false, (b, t) => {
    const curl: LP = { body: [0, 0.5, 0], fore: [0.1, 0.8, 0], head: [0.1, 0.9, 0], hind: [0, -0.8, 0], tail: [0, -0.9, 0] };
    timeline(b, t, [
      [0, L_ST],
      [0.12, { fore: [-0.8, -0.4, 0], head: [-0.4, -0.5, 0], tail: [0.5, 0.5, 0] }, outExpo],
      [0.3, { fore: [-0.2, 0.6, 0], head: [0, 0.7, 0], hind: [0.2, -0.6, 0], tail: [0.2, -0.6, 0] }, inOut],
      [0.6, curl, inOut],
      [1.2, { ...curl, root: [0, 0, 0, 0, -0.03, 0] }, outCubic],
    ]);
    const tw = envelope(t, 0.1, 0.2, 0.5, 0.7) * Math.sin(TAU * 7 * t);
    b.tail.rot(0, 0.3 * tw, 0);
    b.head.rot(0, 0.3 * tw, 0);
    const shr = 1 - 0.3 * span(t, 0.6, 1.2, inCubic);
    for (const n of ['body', 'fore', 'hind'] as const) b[n].scale(shr);
  }, (t) => {
    const k = span(t, 0.6, 1.2, inOut);
    return [1 + 0.1 * k, 1 - 0.45 * k, 1 + 0.1 * k];
  });

export const leechClips = (): Clip[] => [leechIdle(), leechWalk(), leechAttack(), leechHit(), leechStun(), leechDie()] as Clip[];

/* ======================================================================================================== */
/* Boneworm                                                                                                  */
/* ======================================================================================================== */

type WB = 'root' | 'body' | 'tail' | 'neck' | 'head';
type WP = KeyPose<WB>;

const wSway = (b: Bones<WB>, th: number, a: number): void => {
  b.head.rot(0, a * Math.sin(th - 1.8), 0);
  b.neck.rot(0, a * Math.sin(th - 0.9), 0);
  b.body.rot(0, a * 0.5 * Math.sin(th), 0);
  b.tail.rot(0, -a * 1.2 * Math.sin(th + 1.0), 0);
};

const wormIdle = (): Clip<WB> =>
  clip<WB>('idle', 2.0, true, (b, t) => {
    const p = phase(t, 2.0) * TAU;
    wSway(b, p, 0.22);
    // Reared, the mouth turns this way and that, opening (its scale) as it tastes the air.
    b.neck.rot(-0.08 * (1 + Math.sin(p * 2)), 0, 0);
    b.head.rot(0.1 * Math.sin(p * 2 + 1), 0, 0).scale(1 + 0.06 * Math.max(0, Math.sin(p * 3)));
  });

/** The slither: a wave runs down from the reared front to the dragging tail. */
const wormWalk = (dur = 0.5): Clip<WB> =>
  clip<WB>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    wSway(b, th, 0.4);
    b.neck.rot(0.1 + 0.06 * Math.sin(th * 2), 0, 0);
    b.head.rot(0.1 * Math.sin(th * 2 - 0.6), 0, 0);
    b.root.pos(0, 0.008 * Math.abs(Math.sin(th)), 0);
  });

const W_COCK: WP = { body: [-0.1, 0, 0], neck: [-0.55, 0, 0], head: [-0.35, 0, 0], tail: [0.05, 0, 0], root: [0, 0, 0, 0, 0, -0.05] };
const W_BITE: WP = { body: [0.15, 0, 0], neck: [0.55, 0, 0], head: [0.4, 0, 0], tail: [-0.05, 0, 0], root: [0, 0, 0, 0, 0, 0.12] };

const wormAttack = (): Clip<WB> =>
  clip<WB>('attack', 0.5, false, (b, t) => {
    timeline(b, t, [
      [0, W_COCK],
      [0.06, { ...W_COCK, neck: [-0.65, 0, 0] }, outQuad],
      [0.13, W_BITE, inExpo],
      [0.24, { ...W_BITE, root: [0, 0, 0, 0, 0, 0.1] }, outCubic],
      [0.5, {}, inOut],
    ], { head: 0.02 });
    // The mouth gapes as it strikes and snaps shut on the bite.
    b.head.scale(1 + 0.25 * envelope(t, 0, 0.08, 0.1, 0.14) - 0.08 * envelope(t, 0.13, 0.15, 0.2, 0.3));
  }, (t) => thud(t, [[0.13, 0.06]]));

const wormHit = (): Clip<WB> =>
  clip<WB>('hit', 0.3, false, (b, t) => {
    const k = settle(t, 3, 10) * span(t, 0, 0.02, outQuad);
    b.root.pos(0, 0, -0.04 * k);
    b.neck.rot(-0.35 * k, 0.2 * k, 0);
    b.head.rot(-0.3 * k, 0.2 * k, 0);
    b.tail.rot(0, -0.3 * k, 0);
  });

const wormStun = (): Clip<WB> =>
  clip<WB>('stun', 1.5, true, (b, t) => {
    const p = phase(t, 1.5) * TAU;
    wSway(b, p, 0.15);
    b.neck.rot(0.35, 0, 0);
    b.head.rot(0.4, 0, 0.3 * Math.sin(p));
  });

/** It thrashes, coils and sinks back into the floor it came out of. */
const wormDie = (): Clip<WB> =>
  clip<WB>('die', 1.3, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [0.12, { neck: [-0.7, 0.4, 0], head: [-0.5, 0.3, 0], tail: [0, -0.5, 0] }, outExpo],
      [0.35, { neck: [0.2, -0.6, 0], head: [0.3, -0.5, 0], tail: [0, 0.6, 0], body: [0, 0.3, 0] }, inOut],
      [0.6, { neck: [0.6, 0.5, 0], head: [0.6, 0.6, 0], tail: [0, -0.7, 0], body: [0.1, -0.2, 0], root: [0, 0, 0, 0, -0.08, 0] }, inOut],
      [1.3, { neck: [0.8, 0.6, 0], head: [0.8, 0.8, 0], tail: [0, -0.9, 0], body: [0.2, -0.3, 0], root: [0, 0, 0, 0, -0.55, 0] }, inCubic],
    ]);
    b.root.scale(1 - 0.35 * span(t, 0.6, 1.3, inQuad));
  });

export const boneWormClips = (): Clip[] => [wormIdle(), wormWalk(), wormAttack(), wormHit(), wormStun(), wormDie()] as Clip[];

/* ======================================================================================================== */
/* Bipeds: the root scarecrow and the molten                                                                 */
/* ======================================================================================================== */

type BB = 'root' | 'body' | 'chest' | 'head' | 'armL' | 'foreL' | 'armR' | 'foreR' | 'legL' | 'shinL' | 'legR' | 'shinR';
type BP = KeyPose<BB>;

interface BipedStyle {
  /** Walk cycle (s) and stride (rad). */
  walk: number;
  stride: number;
  /** How far the trunk lists from side to side as it walks (rad). */
  list: number;
  /** Stance. */
  stand: BP;
  /** The blow: cocked, landed, and how long the whole attack takes. */
  cocked: BP;
  landed: BP;
  attackDur: number;
  /** Breath period (s). */
  breath: number;
}

const bipedClips = (S: BipedStyle): Clip[] => {
  const idle = clip<BB>('idle', S.breath, true, (b, t) => {
    const p = phase(t, S.breath) * TAU;
    const br = Math.sin(p);
    apply(b, S.stand);
    b.root.pos(0, -0.01 * (1 + br), 0);
    b.chest.rot(-0.03 * br, 0.06 * Math.sin(p * 0.5 * 2), 0.02 * Math.sin(p));
    b.head.rot(0.04 * br, 0.3 * Math.sin(p + 0.7), 0.06 * Math.sin(p));
    b.armL.rot(0.05 * Math.sin(p + 0.3), 0, -0.03 * br);
    b.armR.rot(0.04 * Math.sin(p + 1.2), 0, 0.03 * br);
    b.foreL.rot(-0.06 * Math.sin(p + 0.9));
    b.foreR.rot(-0.05 * Math.sin(p + 1.9));
  }, (t) => {
    const br = Math.sin(phase(t, S.breath) * TAU);
    return [1 + 0.01 * br, 1 - 0.008 * br, 1 + 0.01 * br];
  });

  const walk = clip<BB>('walk', S.walk, true, (b, t) => {
    const th = phase(t, S.walk) * TAU;
    const sn = Math.sin(th), cs = Math.cos(th);
    apply(b, S.stand, 0.6);
    const A = S.stride;
    b.legL.rot(-A * sn - 0.04);
    b.legR.rot(A * sn - 0.04);
    b.shinL.rot(0.75 * Math.max(0, cs) ** 1.5 + 0.06);
    b.shinR.rot(0.75 * Math.max(0, -cs) ** 1.5 + 0.06);
    b.root.rot(0, 0.06 * sn, -S.list * cs).pos(-0.03 * cs, 0.03 * Math.cos(2 * th) - 0.03, 0);
    b.body.rot(0.06, 0.08 * sn, 0);
    b.chest.rot(0.05 + 0.03 * Math.cos(2 * th), -0.14 * sn, S.list * 0.5 * cs);
    b.head.rot(-0.05, 0.1 * sn, 0);
    const sl = Math.sin(th - 0.35);
    b.armL.rot(0.4 * sl, 0, -0.05);
    b.armR.rot(-0.32 * sl, 0, 0.05);
    b.foreL.rot(-0.2 * Math.max(0, -Math.sin(th - 0.7)) - 0.05);
    b.foreR.rot(-0.2 * Math.max(0, Math.sin(th - 0.7)) - 0.05);
  }, (t) => {
    const j = Math.max(0, Math.cos(2 * phase(t, S.walk) * TAU));
    return [1 + 0.015 * j, 1 - 0.02 * j, 1 + 0.015 * j];
  });

  const attack = clip<BB>('attack', S.attackDur, false, (b, t) => {
    timeline(b, t, [
      [0, { ...S.stand, ...S.cocked }],
      [0.06, { ...S.stand, ...S.cocked, chest: [((S.cocked.chest?.[0] ?? 0) - 0.05), (S.cocked.chest?.[1] ?? 0) - 0.05, 0] }, outQuad],
      [0.13, { ...S.stand, ...S.landed }, inExpo],
      [0.4, { ...S.stand, ...S.landed, root: [0, 0, 0, 0, -0.06, 0.08] }, outCubic],
      [S.attackDur, S.stand, inOut],
    ], { head: 0.04, foreR: 0.03 });
    b.root.pos(0.012 * kick(t, 0.13, 7, 8), 0, 0);
  }, (t) => thud(t, [[0.13, 0.05]]));

  const hit = clip<BB>('hit', 0.45, false, (b, t) => {
    apply(b, S.stand);
    const q = settle(t, 2.2, 8) * span(t, 0, 0.03, outQuad);
    b.chest.rot(-0.12 * q, 0.06 * q);
    b.head.rot(-0.25 * q, -0.1 * q, 0.06 * q);
    b.root.pos(0, 0, -0.04 * q);
    b.armL.rot(0.15 * q, 0, -0.1 * q);
    b.armR.rot(0.15 * q, 0, 0.1 * q);
  });

  const stun = clip<BB>('stun', 2.4, true, (b, t) => {
    const p = phase(t, 2.4) * TAU;
    apply(b, S.stand);
    b.root.pos(0, -0.05, 0).rot(0.02 * Math.sin(p), 0, 0.05 * Math.cos(p));
    b.body.rot(0.15, 0, 0);
    b.chest.rot(0.25 + 0.05 * Math.sin(p * 2), 0.1 * Math.sin(p), 0);
    b.head.rot(0.45 + 0.12 * Math.sin(p + 0.8), 0.3 * Math.cos(p + 0.8), 0.2 * Math.sin(p + 0.8));
    b.armL.rot(0.12 * Math.sin(p + 1.4));
    b.armR.rot(-0.12 * Math.sin(p + 1.4));
    b.legL.rot(-0.12);
    b.shinL.rot(0.28);
    b.shinR.rot(0.25);
  });

  /** It staggers, its knees go and it topples onto its face. */
  const die = clip<BB>('die', 2.2, false, (b, t) => {
    const kneel: BP = { legL: [0, 0, -0.1], shinL: [1.5, 0, 0], legR: [0.05, 0, 0.1], shinR: [1.5, 0, 0] };
    timeline(b, t, [
      [0, S.stand],
      [0.25, { root: [0, 0, 0, 0, 0.02, -0.06], body: [-0.15, 0.1, 0], chest: [-0.3, 0.15, 0], head: [-0.6, 0.2, 0.1], armL: [-1.2, 0, -0.5], armR: [-1.0, 0, 0.6], foreL: [-0.4, 0, 0], foreR: [-0.4, 0, 0] }, outExpo],
      [0.8, { ...kneel, root: [0, 0, 0, 0, -0.42, -0.04], body: [0.3, 0, 0.05], chest: [0.3, 0, 0], head: [0.5, 0, 0.25], armL: [-0.1, 0, -0.1], armR: [-0.1, 0, 0.1] }, inQuad],
      [0.95, { ...kneel, root: [0, 0, 0, 0, -0.4, -0.04], body: [0.35, 0, 0.05], chest: [0.32, 0, 0], head: [0.6, 0, 0.28] }, outBack],
      [1.6, { ...kneel, root: [0, 0, 0, 0, -0.5, 0.06], body: [1.3, 0.1, 0.12], chest: [0.3, 0.05, 0.05], head: [0.2, 0.5, 0.6], armL: [-2.2, 0, -0.5], armR: [-2.1, 0, 0.55] }, inCubic],
      [2.2, { ...kneel, root: [0, 0, 0, 0, -0.5, 0.06], body: [1.26, 0.1, 0.12], chest: [0.28, 0.05, 0.05], head: [0.3, 0.6, 0.7], armL: [-2.25, 0, -0.52], armR: [-2.15, 0, 0.57] }, outBack],
    ], { head: 0.06, foreL: 0.04, foreR: 0.04 });
  }, (t) => thud(t, [[0.8, 0.05], [1.6, 0.08]]));

  return [idle, walk, attack, hit, stun, die] as Clip[];
};

/** The root scarecrow: slow and heavy, listing like a tree in wind; it brings its burl down overhead. */
const ROOTWIGHT: BipedStyle = {
  walk: 1.9, stride: 0.32, list: 0.08, breath: 4.2, attackDur: 1.2,
  stand: { body: [0.04, 0, 0], chest: [0.08, 0, 0.03], head: [-0.1, 0, 0.1], armL: [-0.05, 0, -0.08], foreL: [-0.05, 0, 0], armR: [-0.05, 0, 0.06], foreR: [-0.1, 0, 0], legL: [-0.05, 0, -0.03], shinL: [0.08, 0, 0], legR: [0.02, 0, 0.03], shinR: [0.06, 0, 0] },
  cocked: { body: [-0.06, -0.15, 0], chest: [-0.2, -0.3, 0.05], head: [-0.15, 0.25, 0], armR: [-2.6, 0, 0.3], foreR: [-0.7, 0, 0], armL: [-0.3, 0, -0.3], legL: [-0.25, 0, -0.03], legR: [0.15, 0, 0.03], shinR: [0.2, 0, 0] },
  landed: { root: [0, 0, 0, 0, -0.08, 0.08], body: [0.2, 0.12, 0], chest: [0.42, 0.3, -0.05], head: [0.05, -0.2, 0], armR: [-0.6, 0, 0.1], foreR: [-0.1, 0, 0], armL: [0.2, 0, -0.3], legL: [-0.42, 0, -0.04], shinL: [0.4, 0, 0], legR: [0.25, 0, 0.04], shinR: [0.25, 0, 0] },
};

/** The molten: hunched, deliberate, each step a weight of metal; it swings its dripping fist across. */
const MOLTEN: BipedStyle = {
  walk: 1.4, stride: 0.38, list: 0.05, breath: 3.0, attackDur: 0.95,
  stand: { body: [0.06, 0, 0], chest: [0.12, 0, 0], head: [-0.12, 0, 0], armL: [-0.1, 0, -0.06], foreL: [-0.2, 0, 0], armR: [-0.1, 0, 0.06], foreR: [-0.2, 0, 0], legL: [-0.08, 0, -0.02], shinL: [0.12, 0, 0], legR: [-0.04, 0, 0.02], shinR: [0.1, 0, 0] },
  cocked: { body: [-0.04, -0.3, 0], chest: [-0.1, -0.45, 0], head: [-0.1, 0.35, 0], armR: [-1.4, 0, 1.0], foreR: [-1.2, 0, 0], armL: [-0.5, 0, -0.2], foreL: [-0.6, 0, 0], legL: [-0.3, 0, -0.03], shinL: [0.2, 0, 0], legR: [0.15, 0, 0.03] },
  landed: { root: [0, 0, 0, 0, -0.05, 0.1], body: [0.15, 0.25, 0], chest: [0.3, 0.45, 0], head: [0.05, -0.3, 0], armR: [-1.2, 0, -0.4], foreR: [-0.3, 0, 0], armL: [0.3, 0, -0.2], foreL: [-0.4, 0, 0], legL: [-0.4, 0, -0.04], shinL: [0.35, 0, 0], legR: [0.25, 0, 0.04], shinR: [0.2, 0, 0] },
};

/* ======================================================================================================== */
/* Puffball                                                                                                  */
/* ======================================================================================================== */

type PB = 'root' | 'body' | 'cap' | 'legL' | 'legR';

const puffIdle = (): Clip<PB> =>
  clip<PB>('idle', 2.6, true, (b, t) => {
    const p = phase(t, 2.6) * TAU;
    // It breathes: the ball swells, the torn top opens on the out-breath and smokes.
    b.body.rot(0.03 * Math.sin(p), 0, 0.04 * Math.sin(p * 0.5 * 2 + 1)).scale(1 + 0.025 * Math.sin(p));
    b.cap.scale(1 + 0.08 * Math.max(0, Math.sin(p)));
  }, (t) => {
    const s = Math.sin(phase(t, 2.6) * TAU);
    return [1 + 0.02 * s, 1 - 0.015 * s, 1 + 0.02 * s];
  });

/** The waddle: stumpy steps, the ball rolling from side to side over them. */
const puffWalk = (dur = 0.8): Clip<PB> =>
  clip<PB>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    const sn = Math.sin(th), cs = Math.cos(th);
    b.legL.rot(-0.55 * sn, 0, 0);
    b.legR.rot(0.55 * sn, 0, 0);
    b.body.rot(0.06, 0.05 * sn, -0.16 * cs);
    b.root.pos(0.03 * cs, 0.03 * Math.abs(cs) - 0.01, 0);
    b.cap.rot(0, 0, -0.08 * Math.cos(th - 0.6));
  }, (t) => {
    const j = Math.abs(Math.sin(phase(t, dur) * TAU));
    return [1 + 0.05 * (1 - j), 1 - 0.06 * (1 - j), 1 + 0.05 * (1 - j)];
  });

/** It rocks back on its heels and throws its bulk forward at 0.13 s, puffing spores. */
const puffAttack = (): Clip<PB> =>
  clip<PB>('attack', 0.7, false, (b, t) => {
    timeline(b, t, [
      [0, { body: [-0.35, 0, 0], legL: [0.2, 0, 0], legR: [0.2, 0, 0], root: [0, 0, 0, 0, 0, -0.06] }],
      [0.06, { body: [-0.42, 0, 0], legL: [0.25, 0, 0], legR: [0.25, 0, 0], root: [0, 0, 0, 0, 0, -0.07] }, outQuad],
      [0.13, { body: [0.45, 0, 0], legL: [-0.35, 0, 0], legR: [-0.2, 0, 0], root: [0, 0, 0, 0, 0, 0.12] }, inExpo],
      [0.3, { body: [0.35, 0, 0], legL: [-0.3, 0, 0], legR: [-0.15, 0, 0], root: [0, 0, 0, 0, 0, 0.1] }, outCubic],
      [0.7, {}, inOut],
    ]);
    b.cap.scale(1 + 0.35 * envelope(t, 0.12, 0.16, 0.22, 0.45));
  }, (t) => {
    const k = Math.max(0, kick(t, 0.13, 3, 8));
    return [1 + 0.1 * k, 1 - 0.14 * k, 1 + 0.1 * k];
  });

const puffHit = (): Clip<PB> =>
  clip<PB>('hit', 0.4, false, (b, t) => {
    const k = settle(t, 3, 9) * span(t, 0, 0.02, outQuad);
    b.body.rot(-0.25 * k, 0, 0.1 * k);
    b.cap.scale(1 + 0.2 * Math.abs(k));
    b.root.pos(0, 0, -0.04 * k);
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 9));
    return [1 + 0.1 * k, 1 - 0.12 * k, 1 + 0.1 * k];
  });

const puffStun = (): Clip<PB> =>
  clip<PB>('stun', 1.8, true, (b, t) => {
    const p = phase(t, 1.8) * TAU;
    b.body.rot(0.12 + 0.06 * Math.sin(p), 0, 0.12 * Math.cos(p));
    b.legL.rot(-0.15);
    b.legR.rot(0.1);
    b.root.pos(0, -0.02, 0);
  });

/** It swells, shudders and bursts: the skin goes slack and flat, the top blown open. */
const puffDie = (): Clip<PB> =>
  clip<PB>('die', 1.1, false, (b, t) => {
    const swell = span(t, 0, 0.35, inQuad);
    const burst = span(t, 0.38, 0.5, outExpo);
    const shake = envelope(t, 0.1, 0.25, 0.32, 0.38) * Math.sin(TAU * 18 * t);
    b.body.scale(1 + 0.22 * swell - 0.25 * burst).rot(0.04 * shake, 0, 0.06 * shake);
    b.cap.scale(1 + 0.4 * swell + 0.6 * burst - 1.2 * span(t, 0.5, 1.1, outCubic));
    b.legL.rot(0, 0, -0.5 * burst);
    b.legR.rot(0, 0, 0.5 * burst);
    b.root.pos(0, -0.1 * span(t, 0.5, 1.0, outCubic), 0);
  }, (t) => {
    const k = span(t, 0.38, 1.0, outCubic);
    return [1 + 0.45 * k, 1 - 0.7 * k, 1 + 0.45 * k];
  });

export const puffballClips = (): Clip[] => [puffIdle(), puffWalk(), puffAttack(), puffHit(), puffStun(), puffDie()] as Clip[];

/* ======================================================================================================== */
/* Rime                                                                                                      */
/* ======================================================================================================== */

type RB = 'root' | 'body' | 'core' | 'crest' | 'lip';

const rimeIdle = (): Clip<RB> =>
  clip<RB>('idle', 3.2, true, (b, t) => {
    const p = phase(t, 3.2) * TAU;
    b.core.rot(0, p, 0).pos(0, 0.02 * Math.sin(p * 2), 0);
    b.crest.rot(0.04 * Math.sin(p), 0, 0.04 * Math.sin(p + 1));
    b.lip.pos(0, 0, 0.02 * Math.sin(p * 2 + 0.5));
  }, (t) => {
    const s = Math.sin(phase(t, 3.2) * TAU * 2);
    return [1 + 0.03 * s, 1 - 0.04 * s, 1 + 0.03 * s];
  });

/** The crawl: it reaches its lip forward, then hauls the mound after it. */
const rimeWalk = (dur = 1.3): Clip<RB> =>
  clip<RB>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    b.lip.pos(0, 0.01 * Math.sin(th), 0.07 * Math.sin(th)).scale(1 + 0.1 * Math.sin(th));
    b.body.rot(0.04 * Math.sin(th - 0.8), 0, 0.03 * Math.sin(th * 0.5 * 2));
    b.crest.rot(-0.06 * Math.sin(th - 1.4), 0, 0);
    b.core.rot(0, 0.3 * Math.sin(th), 0).pos(0, 0, 0.03 * Math.sin(th - 1.0));
    b.root.pos(0, 0, 0.03 * Math.sin(th - 1.6));
  }, (t) => {
    const s = Math.sin(phase(t, dur) * TAU - 1.0);
    return [1 - 0.04 * s, 1 + 0.06 * s, 1 + 0.08 * s];
  });

/** It rears back and slams its lip down at 0.13 s. */
const rimeAttack = (): Clip<RB> =>
  clip<RB>('attack', 0.8, false, (b, t) => {
    timeline(b, t, [
      [0, { body: [-0.3, 0, 0], lip: [-0.5, 0, 0, 0, 0.12, -0.08], crest: [0.15, 0, 0] }],
      [0.06, { body: [-0.36, 0, 0], lip: [-0.6, 0, 0, 0, 0.14, -0.1], crest: [0.18, 0, 0] }, outQuad],
      [0.13, { body: [0.22, 0, 0], lip: [0.25, 0, 0, 0, -0.04, 0.22, 1.25], crest: [-0.15, 0, 0], root: [0, 0, 0, 0, 0, 0.08] }, inExpo],
      [0.4, { body: [0.15, 0, 0], lip: [0.2, 0, 0, 0, -0.03, 0.18, 1.15], crest: [-0.1, 0, 0], root: [0, 0, 0, 0, 0, 0.06] }, outCubic],
      [0.8, {}, inOut],
    ], { crest: 0.04 });
  }, (t) => {
    const k = Math.max(0, kick(t, 0.13, 3, 8));
    const rear = envelope(t, 0, 0.02, 0.08, 0.13);
    return [1 + 0.12 * k - 0.04 * rear, 1 - 0.16 * k + 0.1 * rear, 1 + 0.12 * k];
  });

const rimeHit = (): Clip<RB> =>
  clip<RB>('hit', 0.4, false, (b, t) => {
    const k = settle(t, 3, 9) * span(t, 0, 0.02, outQuad);
    b.body.rot(-0.15 * k, 0, 0.08 * k);
    b.crest.rot(-0.2 * k, 0, 0);
    b.core.pos(0, 0, -0.04 * k);
  }, (t) => {
    const k = Math.max(0, settle(t, 3, 9));
    return [1 + 0.08 * k, 1 - 0.1 * k, 1 + 0.08 * k];
  });

const rimeStun = (): Clip<RB> =>
  clip<RB>('stun', 2.0, true, (b, t) => {
    const p = phase(t, 2.0) * TAU;
    b.body.rot(0.05 * Math.sin(p), 0, 0.05 * Math.cos(p));
    b.core.rot(0, 0.2 * Math.sin(p), 0);
    b.lip.pos(0, -0.02, -0.04);
  }, (t) => {
    const s = Math.sin(phase(t, 2.0) * TAU * 3);
    return [1.05 + 0.02 * s, 0.92 - 0.02 * s, 1.05 + 0.02 * s];
  });

/** It cracks, shudders and slumps into a flat crust of ice, its crystals toppling. */
const rimeDie = (): Clip<RB> =>
  clip<RB>('die', 1.5, false, (b, t) => {
    const shake = envelope(t, 0, 0.05, 0.3, 0.45) * Math.sin(TAU * 14 * t);
    b.body.rot(0.04 * shake, 0, 0.05 * shake);
    b.crest.rot(0.6 * span(t, 0.4, 1.0, inQuad), 0, 0.3 * span(t, 0.4, 1.0, inQuad)).pos(0, -0.2 * span(t, 0.4, 1.2, inQuad), 0);
    b.core.pos(0, -0.18 * span(t, 0.3, 1.2, inOut), 0).scale(1 - 0.4 * span(t, 0.6, 1.5, inCubic));
    b.lip.pos(0, -0.08 * span(t, 0.3, 1.0), 0.1 * span(t, 0.3, 1.0));
  }, (t) => {
    const k = span(t, 0.3, 1.3, outCubic);
    return [1 + 0.35 * k, 1 - 0.7 * k, 1 + 0.35 * k];
  });

export const rimeClips = (): Clip[] => [rimeIdle(), rimeWalk(), rimeAttack(), rimeHit(), rimeStun(), rimeDie()] as Clip[];

/* ======================================================================================================== */
/* Shardling                                                                                                 */
/* ======================================================================================================== */

type SB = 'root' | 'body' | 'crest' | 'leg0' | 'knee0' | 'leg1' | 'knee1' | 'leg2' | 'knee2' | 'leg3' | 'knee3';
type SP = KeyPose<SB>;
const SL = [0, 1, 2, 3] as const;
const sSide = (i: number): number => (i < 2 ? 1 : -1);
const sFront = (i: number): boolean => i % 2 === 0;
/** Diagonal pairs step together: right front with left back. */
const sPair = (i: number): number => (i === 0 || i === 3 ? 0 : 1);

const sLeg = (i: number, lift: number, stride: number, splay = 0): [[number, number, number], [number, number, number]] => [
  [-0.35 * lift + 0.2 * splay, -sSide(i) * stride * 0.35, 0],
  [0.25 * lift - 0.3 * splay, 0, 0],
];
const sLegs = (fn: (i: number) => readonly [number, number, number?], rest: SP = {}): SP => {
  const p: Record<string, [number, number, number]> = {};
  for (const i of SL) {
    const [lift, stride, splay = 0] = fn(i);
    const [h, k] = sLeg(i, lift, stride, splay);
    p[`leg${i}`] = h;
    p[`knee${i}`] = k;
  }
  return { ...(p as SP), ...rest };
};
const setLeg = (b: Bones<SB>, i: number, lift: number, stride: number, splay = 0): void => {
  const [h, k] = sLeg(i, lift, stride, splay);
  b[`leg${i}` as SB].rot(...h);
  b[`knee${i}` as SB].rot(...k);
};

const shardIdle = (): Clip<SB> =>
  clip<SB>('idle', 2.6, true, (b, t) => {
    const p = phase(t, 2.6) * TAU;
    b.body.rot(0.03 * Math.sin(p), 0.08 * Math.sin(p * 0.5 * 2 + 1), 0.03 * Math.sin(p + 1));
    b.crest.rot(0, 0, 0.03 * Math.sin(p * 2)).scale(1 + 0.03 * Math.sin(p * 2));
    b.root.pos(0, -0.008 * (1 + Math.sin(p)), 0);
    // One leg taps now and then, like a crab feeling the ground.
    const d = phase(t, 2.6);
    const tap = d > 0.5 && d < 0.62 ? Math.sin(((d - 0.5) / 0.12) * Math.PI) : 0;
    setLeg(b, 0, 0.9 * tap, 0.2 * tap);
  });

/** A quick crab's scuttle on diagonal pairs, the shell rocking. */
const shardWalk = (dur = 0.55): Clip<SB> =>
  clip<SB>('walk', dur, true, (b, t) => {
    const ph = phase(t, dur);
    const th = ph * TAU;
    for (const i of SL) {
      const u = (ph + (sPair(i) ? 0.5 : 0)) % 1;
      const swing = u >= 0.5;
      const k = swing ? (u - 0.5) / 0.5 : u / 0.5;
      const stride = swing ? -Math.cos(Math.PI * k) : Math.cos(Math.PI * k);
      const lift = swing ? Math.sin(Math.PI * k) : 0;
      setLeg(b, i, 1.2 * lift, 0.9 * stride * (sFront(i) ? 1 : 0.9));
    }
    b.root.pos(0, 0.015 * Math.abs(Math.sin(2 * th)) - 0.008, 0);
    b.body.rot(0.04 * Math.sin(2 * th), 0.05 * Math.sin(th), 0.07 * Math.sin(th + 0.4));
    b.crest.rot(0, 0, -0.04 * Math.sin(th + 0.9));
  });

const S_REAR: SP = sLegs((i) => (sFront(i) ? [2.4, 0.6, 0] : [0, -0.3, 0.1]), { root: [0, 0, 0, 0, 0.05, -0.05], body: [-0.5, 0, 0], crest: [-0.1, 0, 0] });
const S_SLAM: SP = sLegs((i) => (sFront(i) ? [0.2, 1.0, 0] : [0, -0.5, 0.2]), { root: [0, 0, 0, 0, -0.02, 0.1], body: [0.3, 0, 0], crest: [0.12, 0, 0] });

/** It rears on its back legs and brings the front ones down like picks at 0.13 s. */
const shardAttack = (): Clip<SB> =>
  clip<SB>('attack', 0.6, false, (b, t) => {
    timeline(b, t, [
      [0, S_REAR],
      [0.06, { ...S_REAR, body: [-0.58, 0, 0] }, outQuad],
      [0.13, S_SLAM, inExpo],
      [0.3, { ...S_SLAM, root: [0, 0, 0, 0, -0.01, 0.08] }, outCubic],
      [0.6, {}, inOut],
    ], { crest: 0.03 });
  }, (t) => thud(t, [[0.13, 0.07]]));

const shardHit = (): Clip<SB> =>
  clip<SB>('hit', 0.35, false, (b, t) => {
    const k = settle(t, 3, 10) * span(t, 0, 0.02, outQuad);
    const a = Math.abs(k);
    b.root.pos(0, 0.02 * a, -0.05 * k);
    b.body.rot(-0.2 * k, 0, 0.1 * k);
    b.crest.rot(0.15 * kick(t, 0.03, 4, 9));
    for (const i of SL) setLeg(b, i, 0.5 * a, 0, 0.5 * a);
  });

const shardStun = (): Clip<SB> =>
  clip<SB>('stun', 1.6, true, (b, t) => {
    const p = phase(t, 1.6) * TAU;
    b.root.pos(0, -0.04, 0).rot(0, 0.12 * Math.sin(p), 0.06 * Math.cos(p));
    b.body.rot(0.15, 0, 0);
    for (const i of SL) setLeg(b, i, 0, 0, 0.8);
  });

/** Its legs give, the shell drops and tips, the crest sags apart. */
const shardDie = (): Clip<SB> =>
  clip<SB>('die', 1.3, false, (b, t) => {
    timeline(b, t, [
      [0, {}],
      [0.15, { ...sLegs(() => [1.0, 0, 0]), root: [0, 0, 0, 0, 0.06, 0], body: [-0.3, 0, 0] }, outExpo],
      [0.5, { ...sLegs((i) => [0, 0.2 * (sFront(i) ? 1 : -1), 1.4]), root: [0, 0, 0.1, 0, -0.26, 0], body: [0.15, 0, 0.25], crest: [0.2, 0, 0.3] }, inQuad],
      [0.62, { ...sLegs((i) => [0, 0.2 * (sFront(i) ? 1 : -1), 1.5]), root: [0, 0, 0.14, 0, -0.24, 0], body: [0.12, 0, 0.3], crest: [0.25, 0, 0.35] }, outBack],
      [1.3, { ...sLegs((i) => [0, 0.2 * (sFront(i) ? 1 : -1), 1.6]), root: [0, 0, 0.16, 0, -0.27, 0], body: [0.1, 0, 0.32], crest: [0.3, 0, 0.4, 0, -0.04, 0, 0.85] }, inOut],
    ]);
  }, (t) => thud(t, [[0.5, 0.08]]));

export const shardlingClips = (): Clip[] => [shardIdle(), shardWalk(), shardAttack(), shardHit(), shardStun(), shardDie()] as Clip[];

/* ======================================================================================================== */
/* Scribe                                                                                                    */
/* ======================================================================================================== */

type CB = 'root' | 'body' | 'chest' | 'head' | 'armL' | 'foreL' | 'armR' | 'foreR' | 'scroll';
type CP = KeyPose<CB>;

const C_ST: CP = { chest: [0.04, 0, 0], head: [0.08, 0, 0], armR: [-0.1, 0, 0], foreR: [-0.15, 0, 0], armL: [-0.05, 0, 0], foreL: [-0.05, 0, 0] };

/** Bent over, it writes in the air: the quill moving in small strokes, the head following it. */
const scribeIdle = (): Clip<CB> =>
  clip<CB>('idle', 3.0, true, (b, t) => {
    const p = phase(t, 3.0) * TAU;
    apply(b, C_ST);
    b.root.pos(0, 0.01 * Math.sin(p), 0);
    b.chest.rot(0.03 * Math.sin(p), 0.05 * Math.sin(p * 0.5 * 2), 0);
    b.armR.rot(-0.12 * Math.sin(p * 3), 0.12 * Math.cos(p * 3), 0);
    b.foreR.rot(-0.15 * Math.sin(p * 3 + 0.8), 0, 0);
    b.head.rot(0.04 * Math.sin(p * 3 + 1), -0.12 * Math.cos(p * 3), 0);
    b.scroll.rot(0.04 * Math.sin(p + 0.6), 0, 0.03 * Math.sin(p));
  });

/** It glides: no steps under the robe, only a slow sway and the scroll trailing. */
const scribeWalk = (dur = 1.6): Clip<CB> =>
  clip<CB>('walk', dur, true, (b, t) => {
    const th = phase(t, dur) * TAU;
    apply(b, C_ST);
    b.root.pos(0, 0.02 * Math.sin(th * 2), 0).rot(0, 0.04 * Math.sin(th), 0.03 * Math.sin(th));
    b.body.rot(-0.06, 0, -0.04 * Math.sin(th - 0.5));
    b.chest.rot(0.06, 0.04 * Math.sin(th), 0.03 * Math.sin(th - 0.9));
    b.head.rot(-0.05, 0, -0.02 * Math.sin(th - 1.2));
    b.scroll.rot(-0.18 + 0.06 * Math.sin(th * 2 - 1), 0, 0.05 * Math.sin(th - 1.5));
    b.armR.rot(0.06 * Math.sin(th));
  });

const C_UP: CP = { ...C_ST, chest: [-0.1, -0.2, 0], head: [-0.2, 0.1, 0], armR: [-2.4, 0, 0.3], foreR: [-0.5, 0, 0], armL: [-0.2, 0, -0.1], scroll: [-0.1, 0, 0] };
const C_STROKE: CP = { ...C_ST, chest: [0.3, 0.2, 0], head: [0.2, -0.1, 0], armR: [-0.9, 0, -0.2], foreR: [-0.3, 0, 0], armL: [-0.1, 0, -0.05], root: [0, 0, 0, 0, 0, 0.04] };

/** It raises the quill and slashes a stroke down at 0.13 s (the rune it puts under its mark). */
const scribeAttack = (): Clip<CB> =>
  clip<CB>('attack', 0.9, false, (b, t) => {
    timeline(b, t, [
      [0, C_UP],
      [0.06, { ...C_UP, armR: [-2.55, 0, 0.35] }, outQuad],
      [0.13, C_STROKE, inExpo],
      [0.4, { ...C_STROKE, armR: [-1.0, 0.3, -0.1] }, outCubic],
      [0.9, C_ST, inOut],
    ], { head: 0.04, scroll: 0.08 });
  });

/** It lifts both hands and writes in the air, the scroll lifting off the floor; it closes the rune at 0.9 s. */
const scribeCast = (): Clip<CB> =>
  clip<CB>('cast', 1.4, false, (b, t) => {
    timeline(b, t, [
      [0, C_ST],
      [0.35, { ...C_ST, chest: [-0.2, 0, 0], head: [-0.35, 0, 0], armR: [-2.2, 0, 0.5], foreR: [-0.6, 0, 0], armL: [-1.4, 0, -0.5], foreL: [-0.4, 0, 0], scroll: [-0.5, 0, 0], root: [0, 0, 0, 0, 0.06, 0] }, outCubic],
      [0.9, { ...C_ST, chest: [-0.15, 0, 0], head: [-0.3, 0, 0], armR: [-1.9, 0, 0.2], foreR: [-0.4, 0, 0], armL: [-1.3, 0, -0.4], foreL: [-0.4, 0, 0], scroll: [-0.45, 0, 0], root: [0, 0, 0, 0, 0.08, 0] }, inOut],
      [1.0, { ...C_ST, chest: [0.25, 0, 0], head: [0.15, 0, 0], armR: [-0.8, 0, -0.1], armL: [-0.5, 0, -0.1], scroll: [-0.1, 0, 0] }, inExpo],
      [1.4, C_ST, inOut],
    ], { scroll: 0.1 });
    const writing = envelope(t, 0.35, 0.4, 0.8, 0.9);
    b.armR.rot(0.2 * writing * Math.sin(TAU * 4 * t), 0.2 * writing * Math.cos(TAU * 4 * t), 0);
  });

const scribeHit = (): Clip<CB> =>
  clip<CB>('hit', 0.45, false, (b, t) => {
    apply(b, C_ST);
    const q = settle(t, 2.4, 8) * span(t, 0, 0.03, outQuad);
    b.chest.rot(-0.18 * q, 0.08 * q);
    b.head.rot(-0.3 * q, -0.1 * q, 0.08 * q);
    b.root.pos(0, 0, -0.05 * q);
    b.armR.rot(0.2 * q, 0, 0.15 * q);
    b.armL.rot(0.2 * q, 0, -0.15 * q);
    b.scroll.rot(0.15 * q);
  });

const scribeStun = (): Clip<CB> =>
  clip<CB>('stun', 2.2, true, (b, t) => {
    const p = phase(t, 2.2) * TAU;
    apply(b, C_ST);
    b.root.pos(0, -0.04, 0).rot(0, 0, 0.04 * Math.cos(p));
    b.chest.rot(0.3 + 0.05 * Math.sin(p * 2), 0.1 * Math.sin(p), 0);
    b.head.rot(0.4, 0.3 * Math.cos(p + 0.8), 0.2 * Math.sin(p + 0.8));
    b.armR.rot(0.25);
    b.armL.rot(0.2);
  });

/** It folds down into its own robe: nothing was inside but ink; the robe settles on the floor. */
const scribeDie = (): Clip<CB> =>
  clip<CB>('die', 1.8, false, (b, t) => {
    timeline(b, t, [
      [0, C_ST],
      [0.25, { ...C_ST, chest: [-0.3, 0, 0], head: [-0.5, 0, 0.1], armR: [-1.4, 0, 0.6], armL: [-1.2, 0, -0.6], root: [0, 0, 0, 0, 0.04, 0] }, outExpo],
      [0.9, { chest: [0.7, 0, 0.1], head: [0.6, 0.2, 0.3], armR: [-0.2, 0, 0.2], armL: [-0.2, 0, -0.2], scroll: [0.3, 0, 0.2], root: [0, 0, 0, 0, -0.25, 0], body: [0.1, 0, 0] }, inQuad],
      [1.8, { chest: [1.1, 0, 0.2], head: [0.5, 0.3, 0.4], armR: [0.1, 0, 0.5], armL: [0.1, 0, -0.5], scroll: [0.5, 0, 0.3], root: [0, 0, 0, 0, -0.42, 0], body: [0.2, 0, 0.05] }, outCubic],
    ], { head: 0.06 });
    b.body.scale(1 - 0.3 * span(t, 0.5, 1.8, inOut));
  }, (t) => {
    const k = span(t, 0.6, 1.8, outCubic);
    return [1 + 0.3 * k, 1 - 0.25 * k, 1 + 0.3 * k];
  });

export const scribeClips = (): Clip[] => [scribeIdle(), scribeWalk(), scribeAttack(), scribeHit(), scribeStun(), scribeDie(), scribeCast()] as Clip[];

/** Clips by library kind (see ../rigs.ts). */
export const CLIPS: Record<string, () => Clip[]> = {
  leech: leechClips,
  boneworm: boneWormClips,
  rootwight: () => bipedClips(ROOTWIGHT),
  molten: () => bipedClips(MOLTEN),
  puffball: puffballClips,
  rime: rimeClips,
  shardling: shardlingClips,
  scribe: scribeClips,
};
